import type { RemixIconName } from './types.ts'

// Core icons represent a curated subset of Remix Icons, but their names in Figma may differ.
// Designers sometimes rename Remix icons to more context-appropriate “core” names.
// References:
//   • Figma: https://www.figma.com/design/ZG8O0s0SFTBNkQDdymlLuq/Core-Icons?m=dev
//   • Remix: https://remixicon.com/
//
// The `coreRemixIconNameMap` maps Figma “core” icon names to their corresponding Remix icon names.
//
// If a desired core icon isn’t listed here, add a mapping to its corresponding Remix icon.
// If you need a Remix icon that isn’t part of the core set, contact your neighborhood designer about adding it.
//   — If you know the name they're going to use for the core icon, you may add the mapping in advance.
//
// ⚠️ Do NOT include "*-line" variations as keys.
// The Icon component determines line vs. fill style using the `fill` prop, which selects the proper Remix variant.

// The key is the core icon name (from Figma), and the value is the corresponding pascal cased Remix icon name.
const coreRemixIconNameMap = {
  alert: 'Alert',
  'arrow-down': 'ArrowDown',
  'arrow-left': 'ArrowLeft',
  'arrow-left-right': 'ArrowLeftRight',
  'arrow-right': 'ArrowRight',
  'arrow-up': 'ArrowUp',
  'arrow-up-down': 'ArrowUpDown',
  'bank-card': 'BankCard',
  'checkbox-circle': 'CheckboxCircle',
  'chevron-down': 'ArrowDownS',
  'chevron-left': 'ArrowLeftS',
  'chevron-right': 'ArrowRightS',
  'chevron-up': 'ArrowUpS',
  'circle-arrow-down': 'ArrowDownCircle',
  'circle-arrow-left': 'ArrowLeftCircle',
  'circle-arrow-right': 'ArrowRightCircle',
  'circle-arrow-up': 'ArrowUpCircle',
  'close-circle': 'CloseCircle',
  'delete-bin': 'DeleteBin5',
  'double-chevron-down': 'ArrowDownDouble',
  'double-chevron-left': 'ArrowLeftDouble',
  'double-chevron-right': 'ArrowRightDouble',
  'double-chevron-up': 'ArrowUpDouble',
  'download-2': 'Download2',
  'emotion-happy': 'EmotionHappy',
  'error-warning': 'ErrorWarning',
  'file-check': 'FileCheck',
  'file-zip': 'FileZip',
  'inbox-archive': 'InboxArchive',
  'graduation-cap': 'GraduationCap',
  'lock-close': 'Lock2',
  'loop-left': 'LoopLeft',
  'map-2': 'Map2',
  'menu-kabab': 'More2',
  'menu-meatballs': 'More',
  'money-dollar': 'MoneyDollarCircle',
  'new-tab': 'ShareBox',
  'pause-circle': 'PauseCircle',
  'play-circle': 'PlayCircle',
  'play-large': 'PlayLarge',
  'send-plane-2': 'SendPlane2',
  'shield-check': 'ShieldCheck',
  'star-s': 'StarS',
  'text-align-left': 'AlignLeft',
  attachment: 'Attachment',
  add: 'Add',
  bank: 'Bank',
  bell: 'Bell',
  building: 'Building4',
  calendar: 'Calendar',
  chat: 'Chat4',
  check: 'Check',
  circle: 'CheckboxBlankCircle',
  close: 'Close',
  copy: 'FileCopy',
  database2: 'Database2',
  download: 'Download',
  eye: 'Eye',
  file: 'File',
  film: 'Film',
  filter: 'Filter2',
  flashlight: 'Flashlight',
  forbid: 'Forbid',
  gift: 'Gift',
  global: 'Global',
  image: 'Image',
  information: 'Information',
  link: 'Link',
  mail: 'Mail',
  megaphone: 'Megaphone',
  menu: 'Menu',
  minus: 'Subtract',
  moon: 'Moon',
  music: 'Music',
  notification: 'Notification',
  pencil: 'Pencil',
  phone: 'Phone',
  question: 'Question',
  rocket: 'Rocket',
  search: 'Search',
  sun: 'Sun',
  survey: 'Survey',
  team: 'Team',
  trophy: 'Trophy',
  upload: 'Upload',
  user: 'User',
  video: 'Video',
} as const satisfies Record<string, RemixIconName>

export type CoreIconName = keyof typeof coreRemixIconNameMap

export const CoreIconNames = Object.keys(coreRemixIconNameMap) as CoreIconName[]

export const CoreRemixIconNameMap = coreRemixIconNameMap satisfies Record<
  CoreIconName,
  RemixIconName
>

export const isCoreIconName = (name: unknown): name is CoreIconName => {
  return CoreIconNames.includes(name as CoreIconName)
}
